// Money on screen.
//
// The server holds every amount as a whole number of minor units and the till
// shows it with a decimal point, so this is the one place the two meet. It does
// the conversion by string rather than by dividing: 6500 / 100 is a float, and
// a float is exactly what the rest of this codebase refuses to let money be.

/** Format minor units for a cashier: 6500 with 2 digits reads "65.00". */
export function formatMinor(minor: number, minorDigits: number): string {
	const negative = minor < 0;
	const digits = Math.trunc(Math.abs(minor)).toString();
	let text: string;
	if (minorDigits <= 0) {
		text = digits;
	} else {
		const padded = digits.padStart(minorDigits + 1, '0');
		const whole = padded.slice(0, padded.length - minorDigits);
		const fraction = padded.slice(padded.length - minorDigits);
		text = `${whole}.${fraction}`;
	}
	return negative ? `-${text}` : text;
}

/**
 * Read what a cashier typed into minor units.
 *
 * Only digits count, and they fill the minor places from the right, which is
 * how a till keypad has always worked: typing 6, 5, 0, 0 with two decimal
 * places means 65.00, not 6500.00. Returns null when nothing was typed.
 */
export function parseTypedMinor(typed: string): number | null {
	const digits = typed.replace(/\D/g, '');
	if (digits === '') return null;
	return Number(digits);
}

/** Why a price typed on the dashboard could not be read. */
export type PriceProblem = 'empty' | 'invalid' | 'decimals' | 'negative' | 'large';

export type PriceRead = { ok: true; minor: number } | { ok: false; problem: PriceProblem };

// Arabic-Indic (U+0660) and Eastern Arabic-Indic (U+06F0) digits, the ones an
// Arabic keyboard types, read as the Latin digits they are.
const DIGIT_BLOCKS = [0x0660, 0x06f0];
const ARABIC_DECIMAL = String.fromCodePoint(0x066b);
const ARABIC_THOUSANDS = String.fromCodePoint(0x066c);

function latinDigits(text: string): string {
	let out = '';
	for (const ch of text) {
		const code = ch.codePointAt(0) ?? 0;
		const block = DIGIT_BLOCKS.find((start) => code >= start && code <= start + 9);
		out += block === undefined ? ch : String(code - block);
	}
	return out;
}

/**
 * Read a price typed on the dashboard, as it is written.
 *
 * The till types money into minor places from the right, which suits its
 * keypad; an owner at a keyboard types "50" and means fifty. So here "50" is
 * 50.00, "12.5" is 12.50, Arabic digits and the Arabic decimal mark read the
 * same, and a comma only groups thousands ("1,250"). A price with more places
 * than the currency has is refused rather than rounded, and a minus sign only
 * where the field allows it, such as an option's price change.
 */
export function parsePrice(
	typed: string,
	minorDigits: number,
	options: { negative?: boolean; max?: number } = {},
): PriceRead {
	let text = latinDigits(typed.trim())
		.replaceAll(ARABIC_DECIMAL, '.')
		.replaceAll(ARABIC_THOUSANDS, ',')
		.replaceAll(' ', '');
	if (text === '') return { ok: false, problem: 'empty' };

	let negative = false;
	if (text.startsWith('-') || text.startsWith('−')) {
		negative = true;
		text = text.slice(1);
	}
	// Thousands grouping is allowed only where it is unambiguous: groups of
	// three after the first, before any decimal point.
	if (text.includes(',')) {
		if (!/^\d{1,3}(,\d{3})+(\.\d*)?$/.test(text)) return { ok: false, problem: 'invalid' };
		text = text.replaceAll(',', '');
	}
	const match = /^(\d*)(?:\.(\d*))?$/.exec(text);
	const whole = match?.[1] ?? '';
	const fraction = match?.[2] ?? '';
	if (!match || (whole === '' && fraction === '')) return { ok: false, problem: 'invalid' };
	if (fraction.length > minorDigits) return { ok: false, problem: 'decimals' };
	if (negative && !options.negative) return { ok: false, problem: 'negative' };

	const digits = (whole + fraction.padEnd(minorDigits, '0')).replace(/^0+(?=\d)/, '');
	if (digits.length > 15) return { ok: false, problem: 'large' };
	const magnitude = Number(digits);
	if (magnitude > (options.max ?? Number.MAX_SAFE_INTEGER)) return { ok: false, problem: 'large' };
	return { ok: true, minor: negative && magnitude !== 0 ? -magnitude : magnitude };
}
